import { useRef, useState, useCallback, useEffect } from "react";

/**
 * useDragReorder — Native HTML5 drag-and-drop reorder hook for line-item lists.
 *
 * Kullanım (Formuller / R&D — hammadde satirlarini sürükle-birak siralama):
 *   const dnd = useDragReorder(() => f.lines, (arr) => setF({ ...f, lines: arr }));
 *   <div {...dnd.rowProps(i)} className={dnd.isDropTarget(i) ? "drop-target" : ""}>
 *     <button draggable {...dnd.handleProps(i)}><GripVertical/></button>
 *     ...row content...
 *   </div>
 *
 * Özellikler:
 *  - `isDropTarget(i)` → sürükleme sırasında hover edilen hedef satır.
 *  - `isDragging(i)` → sürüklenen kaynak satır.
 *  - `direction()` → "up" | "down" | null (indicator yönü için).
 *  - **iter297c — Auto-scroll**: Sürükle-bırak esnasında imleç scroll
 *    container'ın üst/alt sınırına yaklaşırsa (default 80px eşik) container
 *    yumuşakça kaydırılır. rAF loop ile pürüzsüz akış; drop/dragend'de durur.
 *    Threshold ve hız `opts` ile ayarlanabilir.
 */
export function useDragReorder(getList, setList, opts = {}) {
  const edge = opts.edge ?? 80;      // px — sınıra yaklaşma eşiği
  const maxSpeed = opts.maxSpeed ?? 18;  // px/frame — kenardayken tam hız

  const dragIdx = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);
  const [srcIdx, setSrcIdx] = useState(null);

  // Auto-scroll state
  const scrollElRef = useRef(null);
  const clientYRef = useRef(null);
  const rafRef = useRef(null);

  const findScrollParent = (el) => {
    let node = el;
    while (node && node !== document.body) {
      const style = getComputedStyle(node);
      const oy = style.overflowY;
      if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  };

  const stopScrollLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    scrollElRef.current = null;
    clientYRef.current = null;
  }, []);

  const tick = useCallback(() => {
    const el = scrollElRef.current;
    const y = clientYRef.current;
    if (!el || y == null || dragIdx.current === null) { rafRef.current = null; return; }
    const rect = el.getBoundingClientRect();
    const topDist = y - rect.top;
    const botDist = rect.bottom - y;
    let dy = 0;
    if (topDist < edge && topDist >= 0) {
      const pct = Math.max(0, Math.min(1, (edge - topDist) / edge));
      dy = -maxSpeed * pct;
    } else if (botDist < edge && botDist >= 0) {
      const pct = Math.max(0, Math.min(1, (edge - botDist) / edge));
      dy = maxSpeed * pct;
    }
    if (dy !== 0) el.scrollTop = Math.max(0, Math.min(el.scrollHeight - el.clientHeight, el.scrollTop + dy));
    rafRef.current = requestAnimationFrame(tick);
  }, [edge, maxSpeed]);

  const ensureScrollLoop = useCallback((rowEl, clientY) => {
    clientYRef.current = clientY;
    if (!scrollElRef.current) scrollElRef.current = findScrollParent(rowEl);
    if (!rafRef.current && scrollElRef.current) rafRef.current = requestAnimationFrame(tick);
  }, [tick]);

  // Bileşen unmount olursa rAF sızıntısı olmasın
  useEffect(() => () => stopScrollLoop(), [stopScrollLoop]);

  const handleProps = useCallback((i) => ({
    onDragStart: (e) => {
      dragIdx.current = i;
      setSrcIdx(i);
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(i));
      } catch (_) {}
    },
    onDragEnd: () => {
      dragIdx.current = null;
      setHoverIdx(null);
      setSrcIdx(null);
      stopScrollLoop();
    },
  }), [stopScrollLoop]);

  const rowProps = useCallback((i) => ({
    onDragOver: (e) => {
      if (dragIdx.current === null || dragIdx.current === i) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (hoverIdx !== i) setHoverIdx(i);
      ensureScrollLoop(e.currentTarget, e.clientY);
    },
    onDragLeave: () => {
      if (hoverIdx === i) setHoverIdx(null);
    },
    onDrop: (e) => {
      const from = dragIdx.current;
      dragIdx.current = null;
      setHoverIdx(null);
      setSrcIdx(null);
      stopScrollLoop();
      if (from === null || from === i) return;
      e.preventDefault();
      const list = getList() || [];
      if (from < 0 || from >= list.length) return;
      const arr = [...list];
      const [moved] = arr.splice(from, 1);
      arr.splice(i, 0, moved);
      setList(arr);
    },
  }), [getList, setList, hoverIdx, ensureScrollLoop, stopScrollLoop]);

  const isDropTarget = useCallback((i) => hoverIdx === i && srcIdx !== null && srcIdx !== i, [hoverIdx, srcIdx]);
  const isDragging = useCallback((i) => srcIdx === i, [srcIdx]);
  const direction = useCallback(() => {
    if (srcIdx === null || hoverIdx === null || srcIdx === hoverIdx) return null;
    return srcIdx > hoverIdx ? "up" : "down";
  }, [srcIdx, hoverIdx]);

  // iter297d — Klavye/dokunmatik alternatifi: ↑ ↓ ok butonlariyla siralama.
  // Aynı listeye drag-drop ile birlikte kullanılabilir; state paylaşımı yok.
  const move = useCallback((from, to) => {
    const list = getList() || [];
    if (from < 0 || to < 0 || from >= list.length || to >= list.length || from === to) return;
    const arr = [...list];
    const [m] = arr.splice(from, 1);
    arr.splice(to, 0, m);
    setList(arr);
  }, [getList, setList]);
  const moveUp = useCallback((i) => move(i, i - 1), [move]);
  const moveDown = useCallback((i) => move(i, i + 1), [move]);

  return { handleProps, rowProps, isDropTarget, isDragging, direction, moveUp, moveDown };
}
